import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Link } from "react-router-dom";
import { ScheduleConsultationDialog } from "@/components/ScheduleConsultationDialog";
import backgroundNoise from "@/assets/background-noise.svg";

const Services = () => {
  const heroReveal = useScrollReveal();
  const servicesReveal = useScrollReveal();
  const principlesReveal = useScrollReveal();
  
  const services = [
    {
      title: "Social Media Marketing",
      subtitle: "Connected Social Growth",
      description: "Creating authentic connections between brands and their customers through creativity, strategy, and data-driven insights that drive meaningful results.",
      link: "/services/social-media"
    },
    {
      title: "Website Design & Development",
      subtitle: "Strategic Digital Presence",
      description: "We blend creative strategy, data-driven insight, and cutting-edge technology to help businesses simplify and scale their website's digital presence.",
      link: "/services/website"
    },
    {
      title: "Integrated Digital Marketing",
      subtitle: "Holistic Digital Growth",
      description: "We combine audience-focused strategies, data-driven insights, and integrated digital tools to create campaigns that engage, convert, and grow.",
      link: "/services/digital-marketing"
    },
    {
      title: "AI Marketing Solutions",
      subtitle: "AI-Driven Marketing Intelligence",
      description: "We combine artificial intelligence with strategic marketing expertise to help businesses work smarter, faster, and more profitably through continuous learning and adaptation.",
      link: "/services/ai"
    },
    {
      title: "Mobile App Development",
      subtitle: "Intelligent Mobile Experiences",
      description: "We help businesses strengthen customer loyalty with apps that create direct, personalized engagement, turning one-time users into long-term brand advocates.",
      link: "/services/mobile-app"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-4 pt-24 md:pt-32 pb-12 md:pb-20 bg-foreground text-background overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundNoise})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          ref={heroReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            heroReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Our <span className="text-accent italic">Services</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-background/80 leading-relaxed max-w-3xl mx-auto">
              At Northquest, we believe sustainable business growth is built through balance, where creativity meets data, and innovation aligns with long-term strategy. Our approach goes beyond driving short-term performance; it focuses on creating digital marketing ecosystems that evolve, adapt, and continue delivering value over time. Every service we provide is designed to strengthen your brand's foundations while expanding its reach and profitability.
            </p>
          </div>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-16 md:py-32 px-4 bg-background">
        <div 
          ref={servicesReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            servicesReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-20">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">Our Services</h2>
              <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto px-4">
                Comprehensive digital marketing solutions designed to drive measurable growth and lasting impact.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {services.map((service, index) => (
                <Link 
                  key={index}
                  to={service.link}
                  className="group"
                >
                  <div className="bg-background border-2 border-border hover:border-accent rounded-3xl p-8 transition-all duration-300 h-full flex flex-col hover:shadow-xl">
                    {/* Number */}
                    <div className="text-5xl font-bold text-accent/20 group-hover:text-accent/60 transition-colors duration-500 mb-6">
                      0{index + 1}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-2 group-hover:text-accent transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-accent font-semibold text-base mb-4">
                        {service.subtitle}
                      </p>
                      <p className="text-muted-foreground text-base leading-relaxed mb-6">
                        {service.description}
                      </p>
                    </div>
                    
                    {/* CTA */}
                    <div className="flex items-center gap-2 text-accent font-semibold group-hover:gap-3 transition-all">
                      <span>Learn more</span>
                      <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="mt-16 md:mt-32 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Ready to Transform Your <span className="text-accent italic">Digital Presence?</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 md:mb-12 leading-relaxed max-w-2xl mx-auto px-4">
              Let's discuss how our services can help you achieve your business goals and drive measurable growth.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
              <ScheduleConsultationDialog
                buttonText="Schedule a Consultation"
                buttonClassName="px-6 md:px-8 py-3 md:py-4 bg-foreground text-background rounded-full text-base md:text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                <button className="px-6 md:px-8 py-3 md:py-4 bg-foreground text-background rounded-full text-base md:text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105">
                  Schedule a Consultation
                </button>
              </ScheduleConsultationDialog>
              <Link to="/about">
                <button className="px-6 md:px-8 py-3 md:py-4 border-2 border-foreground/20 hover:bg-foreground/5 rounded-full text-base md:text-lg font-semibold transition-all">
                  Learn More About Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Guiding Principles */}
      <section 
        className="py-16 md:py-32 px-4 bg-foreground text-background relative overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundNoise})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          ref={principlesReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            principlesReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-center">Our Guiding Principles</h2>
            <p className="text-center text-background/70 text-lg mb-16 max-w-2xl mx-auto">
              Four core values that shape our approach and guide every decision we make.
            </p>
            
            <div className="space-y-0">
              {[
                {
                  number: "01",
                  title: "Growth with Purpose",
                  description: "Every strategy is designed to deliver measurable results today while building the foundation for long-term, sustainable growth."
                },
                {
                  number: "02",
                  title: "Audience at the Center",
                  description: "We put people first, creating experiences and campaigns that connect, engage, and inspire lasting loyalty."
                },
                {
                  number: "03",
                  title: "Creativity Meets Technology",
                  description: "By blending bold ideas with smart digital tools, we craft solutions that stand out, perform, and evolve with your business."
                },
                {
                  number: "04",
                  title: "Collaboration & Transparency",
                  description: "We work as partners, not just providers—building trust through open communication, clear metrics, and ethical practices."
                }
              ].map((principle, index) => (
                <div 
                  key={index}
                  className="group border-t border-white/10 first:border-t-0 py-10 md:py-16 transition-all duration-500 hover:bg-white/5 px-6 md:px-10 -mx-6 md:-mx-10 rounded-2xl"
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center">
                    <div className="md:col-span-2">
                      <div className="text-6xl md:text-8xl font-bold text-accent/30 group-hover:text-accent transition-colors duration-500">
                        {principle.number}
                      </div>
                    </div>
                    
                    <div className="md:col-span-10">
                      <h3 className="text-2xl md:text-4xl font-bold mb-4 group-hover:text-accent transition-colors duration-300">
                        {principle.title}
                      </h3>
                      <p className="text-background/70 text-base md:text-lg leading-relaxed group-hover:text-background/90 transition-colors duration-300">
                        {principle.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
